/**
 * RaceGateStore — client-side record of the active race ability gate.
 *
 * Set/cleared by RaceClientHandler from the `race:abilityGate` /
 * `race:abilityGateClear` broadcasts; consulted at the top of
 * PlayerManager.performAbility (the ONE client cast gate — G-N4).
 * Module singleton on the CooldownStore pattern: survives scene
 * restarts and needs no service wiring.
 *
 * @see docs/design/gm/GM_RACE_CLASS_BALANCE_SPEC.md
 */

interface RaceGateState {
  disabled: Set<string>;
  /** When set, ONLY these ids may be cast (racer-kit parity; empty set =
   *  the staging-countdown total lock). Undefined → denylist-only. */
  allowed?: Set<string>;
}

let gate: RaceGateState | null = null;

export const RaceGateStore = {
  set(disabledAbilities: string[], allowedAbilities?: string[]): void {
    gate = {
      disabled: new Set(disabledAbilities),
      ...(allowedAbilities ? { allowed: new Set(allowedAbilities) } : {}),
    };
  },

  clear(): void {
    gate = null;
  },

  /** True while a race gate is active (between race start and end). */
  isActive(): boolean {
    return gate !== null;
  },

  /** Count of explicitly-allowed ability IDs (the racer kit). null when no
   *  allowlist is set (denylist-only mode). 0 = total lock (staging). */
  getAllowedCount(): number | null {
    return gate?.allowed ? gate.allowed.size : null;
  },

  /** True when the ability is disabled by the active race gate. */
  isDisabled(abilityId: string): boolean {
    if (!gate) return false;
    if (gate.allowed && !gate.allowed.has(abilityId)) return true;
    return gate.disabled.has(abilityId);
  },

  /**
   * True when the active allowlist EXPLICITLY grants this ability. The race
   * kit is cross-class, so allowlisted abilities bypass the class unlock
   * curve (learned gate) — the race gate is the authority while active.
   */
  isExplicitlyAllowed(abilityId: string): boolean {
    return gate?.allowed?.has(abilityId) ?? false;
  },
};
